import React from "react";
import ApplicationProvider from "./application-provider";
import NavigationLayout from "pages/navigation-layout";
import {Flex, rem, Title, Text, SimpleGrid, Box, Divider, Table, Badge, Button} from "@mantine/core";
import {IconInfoCircleFilled} from "@tabler/icons-react";
import ReportInfoCounterCard from "components/report/ReportInfoCounterCard";

interface Props {
    isAuthenticated: string;
    csrfToken: string;
    logoutUrl: string;
    userFirstname: string;
    userLastname: string;
    densityReport: string;
    totals: string
    staticBaseUrl: string;
}

export default function ReportsPage(props: Props) {
    const densityReport = JSON.parse(props.densityReport);
    const totals = JSON.parse(props.totals);

    const [selectedRha, setSelectedRha] = React.useState<number | null>(null);
    const [chnList, setChnList] = React.useState([]);

    interface RhaData {
        V_Population: number;
        OP_Population: number;
        Gap: number;
        Status: string;
        Density: string;

        [key: string]: any;
    }

    function getStatusColor(status: string) {
        if (status === 'Neutral') {
            return '#EFEFEF'
        }

        if (status === 'Need OAPs') {
            return '#249C4D0D'
        }

        if (status === 'Need Vols') {
            return '#F9E6E5'
        }

        if (status === 'Balanced') {
            return '#97BDFF66'
        }
    }

    function getStatusTextColor(status: string) {
        if (status === 'Neutral') {
            return '#1A1D1F'
        }

        if (status === 'Need OAPs') {
            return '#249C4D'
        }

        if (status === 'Need Vols') {
            return '#E1251B'
        }

        if (status === 'Balanced') {
            return '#1A1D1F'
        }
    }

    function selectRha(index: number) {
        if (selectedRha === index) {
            setSelectedRha(null);
            setChnList([])
            return;
        }

        setSelectedRha(index)

        const rhaData = Object.entries(densityReport)[index][1] as RhaData
        const teamsData = Object.entries(rhaData)
            .filter(([key]) => !['V_Population', 'OP_Population', 'Gap', 'Status', 'Density'].includes(key))
            .reduce((acc, [key, value]) => Object.assign(acc, {[key]: value}), {} as { [key: string]: any });

        const allChnData: { [key: string]: any } = {};
        
        Object.entries(teamsData).forEach(([teamKey, teamValue]: [string, any]) => {
            Object.entries(teamValue)
                .filter(([key]) => !['V_Population', 'OP_Population', 'Gap', 'Status', 'Density'].includes(key))
                .forEach(([chnKey, chnValue]: [string, any]) => {
                    if (!allChnData[chnKey]) {
                        allChnData[chnKey] = {
                            OP_Population: 0,
                            V_Population: 0
                        };
                    }
                    
                    allChnData[chnKey].OP_Population = (allChnData[chnKey].OP_Population || 0) + (chnValue.OP_Population || 0);
                    allChnData[chnKey].V_Population = (allChnData[chnKey].V_Population || 0) + (chnValue.V_Population || 0);
                    
                    const op = allChnData[chnKey].OP_Population;
                    const vp = allChnData[chnKey].V_Population;
                    
                    if (op || vp) {
                        allChnData[chnKey].Density = vp ? (op / vp).toFixed(2) : "N/A";
                        allChnData[chnKey].Gap = Math.abs(op - vp);
                        
                        if (op === 0 && vp === 0) {
                            allChnData[chnKey].Status = "Neutral";
                        } else if (op === 0) {
                            allChnData[chnKey].Status = "Need OPs";
                        } else if (vp === 0) {
                            allChnData[chnKey].Status = "Need Vols";
                        } else {
                            const ratio = op / vp;
                            if (ratio > 2) {
                                allChnData[chnKey].Status = "Need Vols";
                            } else if (ratio < 0.5) {
                                allChnData[chnKey].Status = "Need OPs";
                            } else {
                                allChnData[chnKey].Status = "Balanced";
                            }
                        }
                    }
                });
        });

        // @ts-ignore
        setChnList(allChnData);
    }

    const rhaRows = Object.entries(densityReport).map((element: any, index: number) => {
        const name = element[0];
        const data = element[1];
        const status = data.Status
        return (
            <Table.Tr
                key={index}
                bg={selectedRha === index ? '#EFEFEF' : undefined}
                style={{cursor: 'pointer'}}
                onClick={() => selectRha(index)}
            >
                <Table.Td>
                    <Text fz={rem(14)} lh={rem(24)} fw={600} c={'#6F767E'}>
                        {name}
                    </Text>

                </Table.Td>
                <Table.Td>
                    <Badge
                        c={'#FFFFFF'}
                        bg={'#4B49AC'}
                        fw={600}
                        fz={rem(15)}
                        lh={rem(24)}
                        tt={'capitalize'}
                        px={rem(6)}
                        radius="sm"
                        size={'lg'}
                    >{data.OP_Population}</Badge>
                </Table.Td>
                <Table.Td>
                    <Badge
                        c={'#FFFFFF'}
                        bg={'#7978E9'}
                        fw={600}
                        fz={rem(15)}
                        lh={rem(24)}
                        tt={'capitalize'}
                        px={rem(6)}
                        radius="sm"
                        size={'lg'}
                    >{data.V_Population}</Badge>
                </Table.Td>
                <Table.Td>
                    <Badge
                        c={'#1A1D1F'}
                        bg={'#F8DCDA'}
                        fw={600}
                        fz={rem(15)}
                        lh={rem(24)}
                        tt={'capitalize'}
                        px={rem(6)}
                        radius="sm"
                        size={'lg'}
                    >{data.Density}</Badge>
                </Table.Td>
                <Table.Td>
                    <Badge
                        c={'#1A1D1F'}
                        bg={'#EFEFEF'}
                        fw={600}
                        fz={rem(15)}
                        lh={rem(24)}
                        tt={'capitalize'}
                        px={rem(6)}
                        radius="sm"
                        size={'lg'}
                    >{data.Gap}</Badge>

                </Table.Td>
                <Table.Td>
                    <Badge
                        c={getStatusTextColor(status)}
                        bg={getStatusColor(status)}
                        fw={600}
                        fz={rem(15)}
                        lh={rem(24)}
                        px={rem(6)}
                        radius="sm"
                        tt={"capitalize"}
                        size={'lg'}
                    >{status}</Badge>

                </Table.Td>
                <Table.Td>
                    <Button
                        variant={'filled'}
                        bg={'#EFEFEF'}
                        py={0}
                        px={rem(6)}
                        size={'sm'}
                        w={rem(87)}
                        h={rem(24)}
                    >
                        <Text
                            fw={600}
                            fz={rem(15)}
                            lh={rem(24)}
                            tt={"capitalize"}
                            c={'#1A1D1F'}
                        >
                            View CHN
                        </Text>
                    </Button>
                </Table.Td>
            </Table.Tr>
        )
    })

    const chnRows = Object.entries(chnList).map((element: any, index: number) => {
        const name = element[0];
        const data = element[1];
        const status = data.Status

        return (
            <Table.Tr key={index}>
                <Table.Td>
                    <Text fz={rem(14)} lh={rem(24)} fw={600} c={'#6F767E'}>
                        {name}
                    </Text>

                </Table.Td>
                <Table.Td>
                    <Badge
                        c={'#FFFFFF'}
                        bg={'#4B49AC'}
                        fw={600}
                        fz={rem(15)}
                        lh={rem(24)}
                        tt={'capitalize'}
                        px={rem(6)}
                        radius="sm"
                        size={'lg'}
                    >{data.OP_Population}</Badge>
                </Table.Td>
                <Table.Td>
                    <Badge
                        c={'#FFFFFF'}
                        bg={'#7978E9'}
                        fw={600}
                        fz={rem(15)}
                        lh={rem(24)}
                        tt={'capitalize'}
                        px={rem(6)}
                        radius="sm"
                        size={'lg'}
                    >{data.V_Population}</Badge>
                </Table.Td>
                <Table.Td>
                    <Badge
                        c={'#1A1D1F'}
                        bg={'#F8DCDA'}
                        fw={600}
                        fz={rem(15)}
                        lh={rem(24)}
                        tt={'capitalize'}
                        px={rem(6)}
                        radius="sm"
                        size={'lg'}
                    >{data.Density}</Badge>
                </Table.Td>
                <Table.Td>
                    <Badge
                        c={'#1A1D1F'}
                        bg={'#EFEFEF'}
                        fw={600}
                        fz={rem(15)}
                        lh={rem(24)}
                        tt={'capitalize'}
                        px={rem(6)}
                        radius="sm"
                        size={'lg'}
                    >{data.Gap}</Badge>

                </Table.Td>
                <Table.Td>
                    <Badge
                        c={getStatusTextColor(status)}
                        bg={getStatusColor(status)}
                        fw={600}
                        fz={rem(15)}
                        lh={rem(24)}
                        px={rem(6)}
                        radius="sm"
                        tt={"capitalize"}
                        size={'lg'}
                    >{status}</Badge>

                </Table.Td>
            </Table.Tr>
        )
    });

    return (
        <ApplicationProvider>
            <NavigationLayout {...props} mainContainerColor={"#F4F4F4"}>
                <Flex direction='column' gap={rem(24)} p={rem(40)}>
                    <Text fw={600} fz={rem(40)} lh={rem(48)} c={'#272B30'}>Home</Text>
                    <Flex direction='column' gap={rem(8)}>
                        <SimpleGrid cols={3} spacing={rem(8)} w={'100%'}>
                            <Flex
                                direction={'row'}
                                gap={rem(12)}
                                px={rem(12)}
                                py={rem(20)}
                                bg={'#fff'}
                                style={{borderRadius: '12px'}}
                                align={'center'}
                                justify={'flex-start'}
                            >
                                <img src={`${props.staticBaseUrl}alone/images/waving-hand.svg`} alt="👋"/>
                                <Flex direction={'column'}>
                                    <Text fw={700} fz={rem(15)} lh={rem(24)}
                                          c={'#1A1D1F'}>Welcome {props.userFirstname}</Text>
                                    <Text fw={700} fz={rem(15)} lh={rem(24)} c={'#1A1D1F'}>Let's connect great
                                        people!</Text>
                                </Flex>
                            </Flex>

                            <Flex
                                direction={'row'}
                                gap={rem(12)}
                                px={rem(12)}
                                py={rem(20)}
                                bg={'#97BDFF4D'}
                                style={{borderRadius: '12px', textDecoration: 'none'}}
                                align={'center'}
                                justify={'flex-start'}
                                component={'a'}
                                href={'/matching/older_person'}
                            >
                                <img src={`${props.staticBaseUrl}alone/images/match-icon.svg`} alt="Matches"/>
                                <Flex direction={'column'}>
                                    <Text
                                        fw={700}
                                        fz={rem(15)}
                                        lh={rem(24)}
                                        c={'#1A1D1F'}>
                                        View Matches
                                    </Text>
                                </Flex>
                            </Flex>

                            <Flex
                                direction={'row'}
                                gap={rem(12)}
                                px={rem(12)}
                                py={rem(20)}
                                bg={'#97BDFF4D'}
                                style={{borderRadius: '12px', textDecoration: 'none'}}
                                align={'center'}
                                justify={'flex-start'}
                                component={'a'}
                                href={'/heatmap'}
                            >
                                <img src={`${props.staticBaseUrl}alone/images/map-icon.svg`} alt="Map"/>
                                <Flex direction={'column'}>
                                    <Text
                                        fw={700}
                                        fz={rem(15)}
                                        lh={rem(24)}
                                        c={'#1A1D1F'}
                                        style={{textDecoration: 'none !important'}}>
                                        Priority Areas Map
                                    </Text>
                                </Flex>
                            </Flex>
                        </SimpleGrid>

                        <Flex
                            direction={'column'}
                            gap={rem(32)}
                            bg={'#FCFCFC'}
                            style={{borderRadius: '8px'}}
                            p={rem(24)}>
                            <Flex direction={'row'}>
                                <Flex direction={'row'} gap={rem(16)} align={'center'} justify={'space-between'}>
                                    <Box w={rem(16)} h={rem(32)} bg={'#249C4D'} style={{borderRadius: '4px'}}></Box>
                                    <Text fw={600} fz={rem(20)} lh={rem(32)} c={'#1A1D1F'}>Insights</Text>
                                </Flex>

                                {/*TODO: Add filter*/}
                            </Flex>

                            <SimpleGrid cols={4}>
                                <Flex direction='row'>
                                    <ReportInfoCounterCard
                                        label={'Total Older Persons'}
                                        value={totals.Total_OP}
                                        popover={'Total number of older persons registered in ALONE'}
                                    />
                                    <Divider orientation={'vertical'} h={rem(64)}></Divider>
                                </Flex>

                                <Flex direction='row' justify={'space-evenly'}>
                                    <ReportInfoCounterCard
                                        label={'Total Volunteers'}
                                        value={totals.Total_Vol}
                                        popover={'Total number of volunteers registered in ALONE'}
                                    />
                                    <Divider orientation={'vertical'}></Divider>
                                </Flex>

                                <Flex direction='row'>
                                    <ReportInfoCounterCard
                                        label={'Older Person Shortage'}
                                        value={totals.OP_Shortage}
                                        popover={'Number of volunteers needed based on the preferred volunteer to older person ratio'}
                                    />
                                    <Divider orientation={'vertical'}></Divider>
                                </Flex>

                                <Flex direction='row'>
                                    <ReportInfoCounterCard
                                        label={'Volunteer Shortage'}
                                        value={totals.Volunteer_Shortage}
                                        popover={'Number of volunteers who have no match based on the preferred volunteer to older person ratio'}
                                    />
                                </Flex>
                            </SimpleGrid>
                        </Flex>

                        <Flex
                            direction={'column'}
                            gap={rem(32)}
                            p={rem(24)}
                            bg={'#FCFCFC'}
                            style={{borderRadius: '8px'}}
                        >
                            <Flex direction={'row'}>
                                <Flex direction={'row'} gap={rem(16)} align={'center'} justify={'space-between'}>
                                    <Box w={rem(16)} h={rem(32)} bg={'#249C4D66'} style={{borderRadius: '4px'}}></Box>
                                    <Text fw={600} fz={rem(20)} lh={rem(32)} c={'#1A1D1F'}>Regional Health Area</Text>
                                </Flex>

                                {/*TODO: Add filter*/}
                            </Flex>

                            <Table verticalSpacing="md">
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>
                                            <Text fz={rem(13)} lh={rem(16)} fw={600} c={'#6F767E'}>
                                                Week
                                            </Text>
                                        </Table.Th>
                                        <Table.Th>
                                            <Text fz={rem(13)} lh={rem(16)} fw={600} c={'#6F767E'}>
                                                Older Persons
                                            </Text>
                                        </Table.Th>
                                        <Table.Th>
                                            <Text fz={rem(13)} lh={rem(16)} fw={600} c={'#6F767E'}>
                                                Volunteers
                                            </Text>
                                        </Table.Th>
                                        <Table.Th>
                                            <Text fz={rem(13)} lh={rem(16)} fw={600} c={'#6F767E'}>
                                                Ratio O:V
                                            </Text>
                                        </Table.Th>
                                        <Table.Th>
                                            <Text fz={rem(13)} lh={rem(16)} fw={600} c={'#6F767E'}>
                                                Gap
                                            </Text>
                                        </Table.Th>
                                        <Table.Th>
                                            <Text fz={rem(13)} lh={rem(16)} fw={600} c={'#6F767E'}>
                                                Status
                                            </Text>
                                        </Table.Th>
                                        <Table.Th>
                                            <Text fz={rem(13)} lh={rem(16)} fw={600} c={'#6F767E'}>
                                                Action
                                            </Text>
                                        </Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {rhaRows}
                                </Table.Tbody>
                            </Table>
                        </Flex>

                        <Flex
                            direction={'column'}
                            gap={rem(32)}
                            p={rem(24)}
                            bg={'#FCFCFC'}
                            style={{borderRadius: '8px'}}
                        >
                            <Flex direction={'row'}>
                                <Flex direction={'row'} gap={rem(16)} align={'center'} justify={'space-between'}>
                                    <Box w={rem(16)} h={rem(32)} bg={'#249C4D66'} style={{borderRadius: '4px'}}></Box>
                                    <Text fw={600} fz={rem(20)} lh={rem(32)} c={'#1A1D1F'}>Community Healthcare
                                        Network</Text>
                                </Flex>

                                {/*TODO: Add filter*/}
                            </Flex>

                            <Table verticalSpacing="md">
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>
                                            <Text fz={rem(13)} lh={rem(16)} fw={600} c={'#6F767E'}>
                                                Community Health Network
                                            </Text>
                                        </Table.Th>
                                        <Table.Th>
                                            <Text fz={rem(13)} lh={rem(16)} fw={600} c={'#6F767E'}>
                                                Older Persons
                                            </Text>
                                        </Table.Th>
                                        <Table.Th>
                                            <Text fz={rem(13)} lh={rem(16)} fw={600} c={'#6F767E'}>
                                                Volunteers
                                            </Text>
                                        </Table.Th>
                                        <Table.Th>
                                            <Text fz={rem(13)} lh={rem(16)} fw={600} c={'#6F767E'}>
                                                Ratio O:V
                                            </Text>
                                        </Table.Th>
                                        <Table.Th>
                                            <Text fz={rem(13)} lh={rem(16)} fw={600} c={'#6F767E'}>
                                                Gap
                                            </Text>
                                        </Table.Th>
                                        <Table.Th>
                                            <Text fz={rem(13)} lh={rem(16)} fw={600} c={'#6F767E'}>
                                                Status
                                            </Text>
                                        </Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    {chnRows}
                                </Table.Tbody>
                            </Table>
                        </Flex>
                    </Flex>
                </Flex>

            </NavigationLayout>
        </ApplicationProvider>
    )
}